import React, { useEffect, useRef, useState } from 'react';

// ── LINKS ──
const GUMROAD_LINK = 'https://swimformation.lemonsqueezy.com/checkout/buy/bc686616-edd5-4b63-adc0-418ed881851e';
const BOOKING_LINK = 'https://cal.com/swimformation/30min';

// ── HOOK: animate on scroll ──
function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

function Reveal({ children, delay = 0, style = {} }) {
  const [ref, visible] = useInView();
  return (
    <div ref={ref} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(32px)',
      transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`,
      ...style
    }}>
      {children}
    </div>
  );
}

// ── STYLES ──
const s = {
  nav: {
    position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
    background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(8px)',
    borderBottom: '1px solid #E8F0F2',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '0 40px', height: '64px',
  },
  navBrand: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '20px', fontWeight: 700, color: '#1F5D6B',
    letterSpacing: '-0.3px',
  },
  navCta: {
    background: '#F2BE42', color: '#1F5D6B',
    border: 'none', borderRadius: '4px',
    padding: '10px 22px', fontFamily: "'DM Sans', sans-serif",
    fontWeight: 600, fontSize: '14px', cursor: 'pointer',
    transition: 'background 0.2s', textDecoration: 'none',
    whiteSpace: 'nowrap',
  },
  hero: {
    minHeight: '100vh', background: '#FFFFFF',
    display: 'flex', alignItems: 'center',
    padding: '100px 40px 80px',
    position: 'relative', overflow: 'hidden',
  },
  heroAccent: {
    position: 'absolute', top: 0, right: 0,
    width: '45%', height: '100%',
    background: 'linear-gradient(135deg, #F2BE42 0%, #F2BE42 60%, #E8A820 100%)',
    clipPath: 'polygon(15% 0%, 100% 0%, 100% 100%, 0% 100%)',
    zIndex: 0,
  },
  heroAccentOverlay: {
    position: 'absolute', top: 0, right: 0,
    width: '45%', height: '100%',
    background: 'linear-gradient(135deg, rgba(31,93,107,0.08) 0%, rgba(31,93,107,0.15) 100%)',
    clipPath: 'polygon(15% 0%, 100% 0%, 100% 100%, 0% 100%)',
    zIndex: 1,
  },
  heroContent: {
    maxWidth: '1100px', margin: '0 auto', width: '100%',
    display: 'grid', gridTemplateColumns: '1fr 1fr',
    gap: '60px', alignItems: 'center', position: 'relative', zIndex: 2,
  },
  heroTag: {
    display: 'inline-block',
    background: '#1F5D6B', color: '#F2BE42',
    fontSize: '11px', fontWeight: 700,
    letterSpacing: '3px', textTransform: 'uppercase',
    padding: '6px 16px', marginBottom: '28px',
  },
  heroH1: {
    fontFamily: "'Playfair Display', serif",
    fontSize: 'clamp(32px, 5vw, 62px)',
    fontWeight: 900, lineHeight: 1.05,
    color: '#1F5D6B', marginBottom: '24px',
  },
  heroSub: {
    fontSize: '18px', lineHeight: 1.7,
    color: '#5D7B80', maxWidth: '460px',
    marginBottom: '40px', fontWeight: 400,
  },
  heroBtns: { display: 'flex', gap: '16px', flexWrap: 'wrap' },
  btnPrimary: {
    background: '#1F5D6B', color: '#FFFFFF',
    border: 'none', padding: '16px 32px',
    fontFamily: "'DM Sans', sans-serif",
    fontWeight: 600, fontSize: '16px',
    cursor: 'pointer', transition: 'background 0.2s, transform 0.15s',
    textDecoration: 'none', display: 'inline-block',
  },
  btnSecondary: {
    background: 'transparent', color: '#1F5D6B',
    border: '2px solid #1F5D6B', padding: '14px 32px',
    fontFamily: "'DM Sans', sans-serif",
    fontWeight: 600, fontSize: '16px',
    cursor: 'pointer', transition: 'all 0.2s',
    textDecoration: 'none', display: 'inline-block',
  },
  heroRight: {
    background: '#1F5D6B',
    padding: '44px 40px',
    position: 'relative',
  },
  statNumber: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '80px', fontWeight: 900,
    color: '#F2BE42', lineHeight: 1,
    display: 'block', marginBottom: '12px',
  },
  statText: {
    fontSize: '17px', color: 'rgba(255,255,255,0.85)',
    lineHeight: 1.65, marginBottom: '28px',
  },
  statDivider: { width: '40px', height: '3px', background: '#F2BE42', marginBottom: '28px' },
  statQuote: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '19px', fontStyle: 'italic',
    color: '#FFFFFF', lineHeight: 1.5,
  },
  section: (bg = '#FFFFFF') => ({ padding: '100px 40px', background: bg }),
  inner: { maxWidth: '1100px', margin: '0 auto' },
  sectionTag: {
    fontSize: '11px', fontWeight: 700,
    letterSpacing: '3px', textTransform: 'uppercase',
    color: '#F2BE42', marginBottom: '16px', display: 'block',
  },
  sectionTagDark: {
    fontSize: '11px', fontWeight: 700,
    letterSpacing: '3px', textTransform: 'uppercase',
    color: '#1F5D6B', marginBottom: '16px', display: 'block',
  },
  h2White: {
    fontFamily: "'Playfair Display', serif",
    fontSize: 'clamp(28px, 4vw, 48px)',
    fontWeight: 900, color: '#FFFFFF',
    lineHeight: 1.15, marginBottom: '20px',
  },
  h2Teal: {
    fontFamily: "'Playfair Display', serif",
    fontSize: 'clamp(28px, 4vw, 48px)',
    fontWeight: 900, color: '#1F5D6B',
    lineHeight: 1.15, marginBottom: '20px',
  },
  bodyWhite: { fontSize: '17px', color: 'rgba(255,255,255,0.8)', lineHeight: 1.75, maxWidth: '580px' },
  bodyMuted: { fontSize: '17px', color: '#5D7B80', lineHeight: 1.75, maxWidth: '580px' },
  truthGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '2px', marginTop: '60px',
  },
  truthCard: { background: '#EDF6F7', padding: '40px 32px', borderTop: '4px solid #1F5D6B' },
  truthNum: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '52px', fontWeight: 900,
    color: '#D8EDF0', lineHeight: 1, marginBottom: '16px', display: 'block',
  },
  truthTitle: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '19px', fontWeight: 700, color: '#1F5D6B', marginBottom: '12px',
  },
  truthBody: { fontSize: '14px', color: '#5D7B80', lineHeight: 1.7 },
  guideSection: { padding: '100px 40px', background: '#1F5D6B', position: 'relative', overflow: 'hidden' },
  guideGrid: {
    maxWidth: '1100px', margin: '0 auto',
    display: 'grid', gridTemplateColumns: '1fr 1fr',
    gap: '80px', alignItems: 'center',
  },
  guideBox: { background: '#FFFFFF', border: '1px solid #D8EDF0', padding: '44px 40px' },
  priceRow: { display: 'flex', alignItems: 'baseline', gap: '12px', marginBottom: '32px' },
  price: { fontFamily: "'Playfair Display', serif", fontSize: '64px', fontWeight: 900, color: '#1F5D6B', lineHeight: 1 },
  priceLabel: { fontSize: '15px', color: '#5D7B80', lineHeight: 1.4 },
  checkList: { listStyle: 'none', marginBottom: '36px' },
  checkItem: {
    display: 'flex', alignItems: 'flex-start',
    gap: '12px', padding: '10px 0',
    borderBottom: '1px solid #D8EDF0',
    fontSize: '15px', color: '#1A2E30', lineHeight: 1.5,
  },
  checkMark: { color: '#1F5D6B', fontSize: '16px', flexShrink: 0, marginTop: '2px' },
  btnMustard: {
    display: 'block', width: '100%', textAlign: 'center',
    background: '#F2BE42', color: '#1F5D6B',
    border: 'none', padding: '18px 32px',
    fontFamily: "'DM Sans', sans-serif",
    fontWeight: 700, fontSize: '17px',
    cursor: 'pointer', transition: 'background 0.2s', textDecoration: 'none',
  },
  guideNote: { textAlign: 'center', marginTop: '12px', fontSize: '11px', color: '#5D7B80' },
  callSection: { padding: '100px 40px', background: '#F2BE42', position: 'relative', overflow: 'hidden' },
  callGrid: {
    maxWidth: '1100px', margin: '0 auto',
    display: 'grid', gridTemplateColumns: '1fr 1fr',
    gap: '80px', alignItems: 'center',
  },
  callBox: { background: '#1F5D6B', padding: '44px 40px' },
  callItems: { listStyle: 'none', marginBottom: '36px' },
  callItem: {
    display: 'flex', alignItems: 'flex-start',
    gap: '12px', padding: '10px 0',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    fontSize: '15px', color: '#FFFFFF', lineHeight: 1.5,
  },
  btnWhite: {
    display: 'block', width: '100%', textAlign: 'center',
    background: '#F2BE42', color: '#1F5D6B',
    border: 'none', padding: '18px 32px',
    fontFamily: "'DM Sans', sans-serif",
    fontWeight: 700, fontSize: '17px',
    cursor: 'pointer', transition: 'background 0.2s', textDecoration: 'none',
  },
  footer: { background: '#174857', padding: '40px 40px', textAlign: 'center' },
  footerBrand: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '22px', fontWeight: 700, color: '#F2BE42', marginBottom: '16px',
  },
  footerDisclaimer: {
    fontSize: '11px', color: 'rgba(255,255,255,0.3)',
    lineHeight: 1.7, maxWidth: '640px', margin: '0 auto',
  },
};

export default function App() {
  return (
    <div>
      {/* NAV */}
      <nav style={s.nav} className="nav-pad">
        <span style={s.navBrand}>Swimformation</span>
        <a href={GUMROAD_LINK} style={s.navCta}>Get the Guide — $49</a>
      </nav>

      {/* HERO */}
      <section style={s.hero} className="hero-section">
        <div style={s.heroAccent} className="hero-accent" />
        <div style={s.heroAccentOverlay} className="hero-accent-overlay" />
        <div style={s.heroContent} className="hero-content">
          <div style={{ opacity: 0, animation: 'fadeUp 0.6s ease 0.1s forwards' }}>
            <span style={s.heroTag}>Texas Homeowner Guide</span>
            <h1 style={s.heroH1} className="hero-h1">
              You already<br />want a pool.<br />
              <span style={{ color: '#F2BE42' }}>You just talked<br />yourself out of it.</span>
            </h1>
            <p style={s.heroSub} className="hero-sub">
              Before you called a builder. Before you asked a question. You saw a number in your head — and decided the answer was no. That number was probably wrong.
            </p>
            <div style={s.heroBtns} className="hero-btns">
              <a href={GUMROAD_LINK} style={s.btnPrimary}>Get the Guide — $49</a>
              <a href={BOOKING_LINK} style={s.btnSecondary}>Book a Call — $125</a>
            </div>
          </div>
          <div style={{ opacity: 0, animation: 'fadeUp 0.6s ease 0.3s forwards' }}>
            <div style={s.heroRight} className="hero-right">
              <span style={s.statNumber} className="stat-number">~90%</span>
              <p style={s.statText}>
                of pool buyers last year used some form of financing. The family who wrote a check is the exception — not the rule.
              </p>
              <div style={s.statDivider} />
              <p style={s.statQuote}>
                "The question was never whether you could afford a pool. It was whether anyone ever showed you what it actually costs monthly."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* TRUTH SECTION */}
      <section style={s.section('#FFFFFF')} className="section-pad">
        <div style={s.inner}>
          <Reveal>
            <span style={s.sectionTagDark}>What's really going on</span>
            <h2 style={s.h2Teal} className="section-h2">
              The barrier isn't money.<br />It's a knowledge gap.
            </h2>
            <p style={{ ...s.bodyMuted, marginBottom: 0 }}>
              Every year, Texas homeowners skip a pool they could have had — not because the money wasn't there, but because nobody ever showed them how it actually works. The project total feels impossible. The monthly payment rarely does.
            </p>
          </Reveal>
          <div style={s.truthGrid} className="truth-grid">
            {[
              { n: '01', title: 'You assumed the answer was no', body: 'Most people never call a pool builder because they\'ve already made the decision for them. Before anyone shows them a single number.' },
              { n: '02', title: 'The project total isn\'t what you pay', body: 'Most pool buyers don\'t write a check for the full amount. They pay monthly — and when they see that number for the first time, everything changes.' },
              { n: '03', title: 'You don\'t know what you qualify for', body: 'There are financing options most homeowners have never heard of — with terms that might surprise you. That\'s what this guide is for.' },
            ].map((item, i) => (
              <Reveal key={i} delay={i * 0.12}>
                <div style={s.truthCard}>
                  <span style={s.truthNum}>{item.n}</span>
                  <div style={s.truthTitle}>{item.title}</div>
                  <p style={s.truthBody}>{item.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section style={{ ...s.section('#1F5D6B') }} className="section-pad">
        <div style={{ ...s.inner, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '80px', alignItems: 'center' }} className="section-body-inner">
          <Reveal>
            <span style={s.sectionTag}>What this is</span>
            <h2 style={s.h2White} className="section-h2">
              The guide nobody in the pool industry gives you for free.
            </h2>
            <p style={s.bodyWhite}>
              Swimformation isn't a sales pitch. It isn't from a bank or a lender. It's straight information — from someone who spent years inside the pool industry in Texas watching families miss out on something they could have had, because nobody took the time to explain how the money actually works.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div style={{ borderLeft: '4px solid #F2BE42', paddingLeft: '32px' }}>
              <p style={{ fontFamily: "'Playfair Display', serif", fontSize: '22px', fontStyle: 'italic', color: '#FFFFFF', lineHeight: 1.6, marginBottom: '24px' }}>
                "I've helped hundreds of Texas homeowners get funded. Most of them had no idea how close they were — until someone showed them the real numbers."
              </p>
              <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)', letterSpacing: '1px', textTransform: 'uppercase' }}>
                Pool Industry Professional · Austin & San Antonio, TX
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* GUIDE OFFER */}
      <section style={s.guideSection} id="buy" className="guide-section-pad">
        <div style={s.guideGrid} className="guide-grid">
          <Reveal>
            <span style={s.sectionTag}>The Playbook</span>
            <h2 style={s.h2White} className="section-h2">
              Everything you need to know.<br />
              <span style={{ color: '#F2BE42' }}>Nothing you don't.</span>
            </h2>
            <p style={{ ...s.bodyWhite, marginBottom: '32px' }}>
              A straight, no-jargon breakdown of what Texas homeowners need to understand before financing a pool. What options exist. What lenders look at. What most people get wrong. How to put yourself in the best position before you apply.
            </p>
            <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.45)', fontStyle: 'italic' }}>
              Instant download · Yours to keep · Educational only, not financial advice
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div style={s.guideBox} className="cta-box">
              <div style={s.priceRow}>
                <span style={s.price}>$49</span>
                <span style={s.priceLabel}>One-time purchase<br />Instant download</span>
              </div>
              <ul style={s.checkList}>
                {[
                  'How pool financing actually works',
                  'What lenders evaluate — and why it matters',
                  'The mistakes that cost homeowners thousands',
                  'How to prepare before you apply',
                  'A pre-application checklist',
                ].map((item, i) => (
                  <li key={i} style={{ ...s.checkItem, borderBottom: i === 4 ? 'none' : '1px solid #D8EDF0' }}>
                    <span style={s.checkMark}>✔</span>
                    {item}
                  </li>
                ))}
              </ul>
              <a href={GUMROAD_LINK} style={s.btnMustard}>Get the Guide — $49</a>
              <p style={s.guideNote}>Not financial advice · Educational purposes only</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CALL OFFER */}
      <section style={s.callSection} id="call" className="guide-section-pad">
        <div style={s.callGrid} className="call-grid">
          <Reveal>
            <div style={s.callBox} className="cta-box">
              <div style={s.priceRow}>
                <span style={{ ...s.price, color: '#F2BE42' }}>$125</span>
                <span style={{ ...s.priceLabel, color: 'rgba(255,255,255,0.6)' }}>30-Minute Strategy Call<br />Texas Homeowners</span>
              </div>
              <ul style={s.callItems}>
                {[
                  'Review your specific numbers: credit, equity, income, project cost',
                  'Identify which loan type fits your situation',
                  'Walk through application strategy for best approval odds and rates',
                  'Get matched with the right lenders for your profile',
                  'Leave with a clear next step — not more questions',
                ].map((item, i) => (
                  <li key={i} style={{ ...s.callItem, borderBottom: i === 4 ? 'none' : '1px solid rgba(255,255,255,0.1)' }}>
                    <span style={{ color: '#F2BE42', fontSize: '16px', flexShrink: 0 }}>✔</span>
                    {item}
                  </li>
                ))}
              </ul>
              <a href={BOOKING_LINK} style={s.btnWhite}>Book the Call — $125</a>
              <p style={{ ...s.guideNote, color: 'rgba(255,255,255,0.3)' }}>Not financial advice · Texas homeowners only</p>
            </div>
          </Reveal>
          <Reveal delay={0.15} style={{ order: 0 }}>
            <div className="call-grid-reverse">
              <span style={{ ...s.sectionTagDark, color: '#1F5D6B' }}>The 1-on-1 Call</span>
              <h2 style={{ ...s.h2Teal, color: '#1F5D6B' }} className="section-h2">
                The guide answers<br />"How does this work?"<br />
                <span style={{ opacity: 0.5 }}>The call answers</span><br />
                "What does this look<br />like for <em>me</em>?"
              </h2>
              <p style={{ ...s.bodyMuted, color: 'rgba(31,93,107,0.85)' }}>
                In 30 minutes you'll have a clear picture of where you stand, which path makes sense for your situation, and exactly what to do next. No sales pitch on the other end. Just a straight conversation with someone who has been through this hundreds of times.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={s.footer} className="footer-pad">
        <div style={s.footerBrand}>Swimformation</div>
        <p style={s.footerDisclaimer}>
          This site is for educational purposes only and does not constitute financial advice. The author is not a licensed financial advisor, mortgage broker, or lender. All financing decisions should be made in consultation with a qualified financial professional. Loan terms, rates, and availability vary by lender and individual circumstances.
        </p>
      </footer>
    </div>
  );
}
